import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BEAGLELOGO from '../../static/beagle-gpt-logo.png';
import { setOpenModal } from '../../utils/appSlice';
import { BASEURL, STORAGE_KEYS } from '../../utils/constants';
import PropTypes from 'prop-types';

/**
 * ModalComponent Module
 * @module ModalComponent
 */
/**
 * ModalComponent - A modal component that is used to render the teams
 * channel in an iframe.
 *
 * @param {string} teamsUrl - The URL of the teams channel to render in the iframe.
 *
 * @returns {React.ReactElement} - The modal component.
 *
 * @example
 * <ModalComponent teamsUrl="https://example.com/teams-channel" />
 */
function ModalComponent({ teamsUrl }) {
  const [loaderActive, setLoaderActive] = useState(true);
  const isModalOpen = useSelector((store) => store.app.isModalOpen);
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  const postMessageTimersRef = useRef([]);
  const messageSentRef = useRef(false);


  useEffect(() => {
    const {add_on_base_url} = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION))
    const handler = function(event) {
      if(event.data  && event.data === "message-recieved" && ((event.origin + "/") === add_on_base_url)){
        dispatch(setOpenModal(false))
      }
      // Iframe signals it's ready — send data immediately
      if (event.data === "iframe-ready" || event.data?.type === "ready") {
        messageSentRef.current = true;
        postMessageTimersRef.current.forEach(id => clearTimeout(id));
        postMessageTimersRef.current = [];
        sendPostMessage();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  },[])

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    messageSentRef.current = false;
    // Clear any pending retry timers
    postMessageTimersRef.current.forEach(id => clearTimeout(id));
    postMessageTimersRef.current = [];

    const onLoad = () => {
      sendPostMessageWithRetry();
    };

    iframe.src = teamsUrl;
    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);
      iframe.src = "";
      postMessageTimersRef.current.forEach(id => clearTimeout(id));
      postMessageTimersRef.current = [];
    };
  }, [isModalOpen]);

  function sendPostMessageWithRetry(attempt = 0) {
    const MAX_RETRIES = 5;
    const RETRY_DELAYS = [0, 500, 1000, 2000, 3000]; // immediate, then 0.5s, 1s, 2s, 3s

    if (attempt >= MAX_RETRIES) {
      setLoaderActive(false);
      return;
    }

    const delay = RETRY_DELAYS[attempt] || 1000;
    const timerId = setTimeout(() => {
      if (messageSentRef.current) return;
      sendPostMessage();
      // Schedule next retry only if send didn't succeed
      if (!messageSentRef.current) {
        sendPostMessageWithRetry(attempt + 1);
      }
    }, delay);
    postMessageTimersRef.current.push(timerId);
  }

  /**
   * @inner
   * @function sendPostMessage
   * @description Sends a postMessage to the iframe with the stored data from localStorage and
   * the parent URL.
   */
  function sendPostMessage() {
    if ( !iframeRef?.current?.contentWindow) {
      return;
    }

    const storedData = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (!storedData) {
      return;
    }

    const info = {
      ...JSON.parse(storedData),
      parentUrl: BASEURL,
      latitude: null,
      longitude: null
    };

    try {
      iframeRef.current.contentWindow.postMessage(info, info.add_on_base_url);
      messageSentRef.current = true;
      // Cancel remaining retry timers — message delivered
      postMessageTimersRef.current.forEach(id => clearTimeout(id));
      postMessageTimersRef.current = [];
      setLoaderActive(false);
    } catch {
      setLoaderActive(false);
    }
  }

  if (!isModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex flex-col rounded-lg shadow-xl w-[95%] max-w-6xl overflow-hidden"
        style={{ background: 'var(--bg-primary)', height: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3">
          <img src={BEAGLELOGO} alt="Beagle logo" className="h-[30px] w-[30px] rounded border border-gray-300" />
          <div className="text-sm leading-4">
            <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Beagle GPT</div>
            <div style={{ color: 'var(--text-primary)' }}>Beagle</div>
          </div>
          <button
            onClick={() => dispatch(setOpenModal(false))}
            className="ml-auto p-1 rounded-full transition-opacity opacity-70 hover:opacity-100"
            style={{ color: 'var(--text-primary)' }}
            aria-label="Close modal"
            data-testid="modal-close-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {loaderActive && (
            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: 'var(--bg-primary)' }}>
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                <span style={{ color: 'var(--text-secondary)' }}>Loading</span>
              </div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            allow="autoplay geolocation"
            title="iframe"
            id="ifr"
            allowFullScreen={true}
            className="border-none rounded-b-lg"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            src={""}
          ></iframe>
        </div>
      </div>
    </div>
  );
}

ModalComponent.propTypes = {
  teamsUrl: PropTypes.string,
};

export default ModalComponent;
